import { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Reply, Pencil, Check, X } from 'lucide-react';
import {
  Bubble,
  MsgContent,
  TimeSpan,
  SwipeWrap,
  SwipeReplyIcon,
  ReplyTriggerBtn,
  EditTriggerBtn,
  EditedTag,
  EditComposerWrap,
  EditTextarea,
  EditActionsRow,
  EditCancelBtn,
  EditSaveBtn,
  QuotedPreview,
  QuotedSender,
  QuotedContent,
} from './ChatWidget.styles';
import { formatMessageDate } from '../utils/formatMessageDate';
import type { MessageSender } from '../api/widget';

const SWIPE_MAX = 60;
const SWIPE_TRIGGER_THRESHOLD = 45;

export interface WidgetDisplayMessage {
  id: string;
  content: string;
  sender_type: 'visitor' | 'agent' | 'system';
  created_at: string;
  sender?: MessageSender;
  reply_to?: { id: string; content: string; sender_type: string; sender_name?: string | null } | null;
  edited_at?: string | null;
}

interface MessageBubbleProps {
  message: WidgetDisplayMessage;
  isVisitor: boolean;
  imageOnly: boolean;
  grouped: boolean;
  sanitizedHtml: string;
  enableSwipe: boolean;
  highlighted: boolean;
  onReply: (message: WidgetDisplayMessage) => void;
  onQuoteClick: (id: string) => void;
  canEdit: boolean;
  isEditing: boolean;
  editingValue: string;
  onEditingChange: (value: string) => void;
  onStartEdit: (message: WidgetDisplayMessage) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  savingEdit: boolean;
}

export default function MessageBubble({
  message,
  isVisitor,
  imageOnly,
  grouped,
  sanitizedHtml,
  enableSwipe,
  highlighted,
  onReply,
  onQuoteClick,
  canEdit,
  isEditing,
  editingValue,
  onEditingChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  savingEdit,
}: MessageBubbleProps) {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (e.dir !== 'Right') return;
      setSwiping(true);
      setSwipeX(Math.min(e.deltaX, SWIPE_MAX));
    },
    onSwiped: (e) => {
      setSwiping(false);
      setSwipeX(0);
      if (e.dir === 'Right' && e.deltaX > SWIPE_TRIGGER_THRESHOLD) {
        onReply(message);
      }
    },
    trackMouse: false,
    preventScrollOnSwipe: true,
  });

  const quotedSenderName =
    message.reply_to?.sender_type === 'agent'
      ? message.reply_to.sender_name ?? 'Agent'
      : message.reply_to?.sender_type === 'visitor'
        ? 'Vous'
        : null;

  const editSaveDisabled = savingEdit || !editingValue.trim();

  return (
    <SwipeWrap
      id={`widget-msg-${message.id}`}
      $swipeX={swipeX}
      $swiping={swiping}
      $highlighted={highlighted}
      {...(enableSwipe ? swipeHandlers : {})}
    >
      {enableSwipe && (
        <SwipeReplyIcon $isVisitor={isVisitor} $opacity={Math.min(swipeX / SWIPE_TRIGGER_THRESHOLD, 1)} aria-hidden="true">
          <Reply size={16} />
        </SwipeReplyIcon>
      )}
      <Bubble $isVisitor={isVisitor} $imageOnly={imageOnly} $grouped={grouped}>
        {message.reply_to && (
          <QuotedPreview type="button" onClick={() => onQuoteClick(message.reply_to!.id)}>
            {quotedSenderName && <QuotedSender>{quotedSenderName}</QuotedSender>}
            <QuotedContent>{message.reply_to.content.replace(/<[^>]*>/g, ' ').trim().slice(0, 140)}</QuotedContent>
          </QuotedPreview>
        )}
        {isEditing ? (
          <EditComposerWrap>
            <EditTextarea
              autoFocus
              rows={1}
              value={editingValue}
              onChange={(e) => onEditingChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSaveEdit();
                }
              }}
            />
            <EditActionsRow>
              <EditCancelBtn type="button" onClick={onCancelEdit} disabled={savingEdit}>
                <X size={12} /> Annuler
              </EditCancelBtn>
              <EditSaveBtn type="button" onClick={onSaveEdit} disabled={editSaveDisabled}>
                <Check size={12} /> {savingEdit ? 'Envoi…' : 'Enregistrer'}
              </EditSaveBtn>
            </EditActionsRow>
          </EditComposerWrap>
        ) : (
          <MsgContent dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        )}
        <TimeSpan $onPrimary={isVisitor && !imageOnly}>
          {formatMessageDate(message.created_at)}
          {message.edited_at && <EditedTag>· modifié</EditedTag>}
        </TimeSpan>
      </Bubble>
      {!isEditing && (
        <>
          {!enableSwipe && (
            <ReplyTriggerBtn
              type="button"
              className="reply-trigger-btn"
              title="Répondre"
              aria-label="Répondre à ce message"
              onClick={() => onReply(message)}
            >
              <Reply size={12} />
            </ReplyTriggerBtn>
          )}
          {canEdit && (
            <EditTriggerBtn
              type="button"
              className="edit-trigger-btn"
              $alwaysVisible={enableSwipe}
              title="Modifier"
              aria-label="Modifier ce message"
              onClick={() => onStartEdit(message)}
            >
              <Pencil size={12} />
            </EditTriggerBtn>
          )}
        </>
      )}
    </SwipeWrap>
  );
}
